import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {isStaff} from '@github-ui/stats'
import {CommandIconButton} from '@github-ui/ui-commands'
import {
  ArrowLeftIcon,
  BeakerIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CommentDiscussionIcon,
  EyeClosedIcon,
  HistoryIcon,
  KebabHorizontalIcon,
  NoteIcon,
  PlusIcon,
  ScreenFullIcon,
  ToolsIcon,
  TrashIcon,
  XIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, Heading, IconButton, Label} from '@primer/react'
import {Box, Button} from '@primer/styled-react'
import {type RefObject, useCallback, useMemo, useRef, useState} from 'react'

import {getCustomInstructionsFromReferences} from '../utils/copilot-chat-helpers'
import {type CopilotChatThread, DialogType} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {getSelectedThread} from '../utils/get-selected-thread'
import {ConversationFeedbackDialog, type FeedbackDialogRef} from './ConversationFeedbackDialog'
import styles from './Header.module.css'
import {PersonalInstructionsDialog} from './PersonalInstructionsDialog'
import {ActionMenuOverlay} from './PortalContainerUtils'
import {StaffDialogs} from './StaffDialogs'

export interface HeaderProps {
  staffDialogRef: React.MutableRefObject<HTMLDivElement | null>
  showStaffDialog: DialogType
  setShowStaffDialog: (value: DialogType) => void
  isImmersive?: boolean
  isResponding?: boolean
}

export const Header = (props: HeaderProps) => {
  const state = useChatState()
  const manager = useChatManager()
  const [showPersonalInstructionsDialog, setShowPersonalInstructionsDialog] = useState(false)

  const thread = getSelectedThread(state)
  const {showTopicPicker, messages, streamingMessage} = state
  const threadName = thread?.name ?? state.restoredThreadTitle

  const handleThreadDelete = useCallback(async () => {
    return thread && manager.deleteThread(thread)
  }, [thread, manager])

  const lifecycleLabelNameEnabled = isFeatureEnabled('lifecycle_label_name_updates')
  const noFloatingButton = isFeatureEnabled('copilot_no_floating_button')

  const menuAnchorRef = useRef<HTMLButtonElement>(null)

  return (
    <Box
      sx={{
        pl: state.mode === 'immersive' ? 1 : 3,
        borderBottom: showTopicPicker ? 'none' : '1px solid',
      }}
      className={styles.HeaderContainer}
    >
      <div className={styles.headerTitleContainer}>
        {state.currentView === 'thread' ? (
          !showTopicPicker &&
          !props.isImmersive &&
          messages.length === 0 &&
          state.chatIsOpen && (
            <Heading as="h2" className={styles.HeaderTitle}>
              {state.currentView === 'thread' ? (
                <>
                  {threadName ? (
                    <span className="Truncate">
                      {/* Primer tooltips are broken in portals, so we need to use a title here */}
                      {/* eslint-disable-next-line github/a11y-no-title-attribute */}
                      <span className="Truncate-text" title={threadName}>
                        {threadName}
                      </span>
                    </span>
                  ) : props.isResponding && !state.chatIsOpen ? (
                    <>Responding…</>
                  ) : !state.chatIsOpen ? (
                    <>Ask Copilot</>
                  ) : (
                    !showTopicPicker && <>New conversation</>
                  )}
                </>
              ) : (
                'Copilot'
              )}
            </Heading>
          )
        ) : (
          <ReturnToCurrentThreadButton />
        )}
      </div>
      <div className={styles.headerActions}>
        {state.chatIsOpen && (
          <>
            {state.renderBetaLabel &&
              (lifecycleLabelNameEnabled ? (
                <BetaLabel className="mr-2" />
              ) : (
                <Label variant="success" className={styles.betaLabel}>
                  Beta
                </Label>
              ))}
            {state.currentView === 'thread' ? (
              <>
                <IconButton
                  hidden={messages.length === 0}
                  variant="invisible"
                  icon={PlusIcon}
                  aria-label="New conversation"
                  tooltipDirection="s"
                  onClick={async () => {
                    await manager.selectThread(null)
                  }}
                  className={styles.actionButton}
                />
                <ThreadOptionButton
                  anchorRef={menuAnchorRef}
                  handleDelete={handleThreadDelete}
                  thread={thread}
                  setShowStaffDialog={props.setShowStaffDialog}
                  setShowPersonalInstructionsDialog={setShowPersonalInstructionsDialog}
                />
              </>
            ) : (
              <IconButton
                variant="invisible"
                icon={PlusIcon}
                aria-label="New conversation"
                tooltipDirection="w"
                onClick={async () => {
                  await manager.selectThread(null)
                }}
                className={styles.actionButton}
              />
            )}
          </>
        )}

        {!props.isImmersive && (
          <>
            {state.chatIsOpen && <div className={styles.buttonSeparator} />}
            <CommandIconButton
              commandId="copilot-chat:continue-in-immersive"
              variant="invisible"
              icon={ScreenFullIcon}
              aria-label="Continue in immersive"
              disabled={!!streamingMessage}
              tooltipDirection="sw"
              className={styles.actionButton}
            />
            {noFloatingButton ? (
              <CommandIconButton
                commandId="copilot-chat:close-assistive"
                variant="invisible"
                icon={XIcon}
                tooltipDirection="sw"
                className={styles.actionButton}
              />
            ) : (
              <IconButton
                variant="invisible"
                icon={state.chatIsOpen ? ChevronDownIcon : ChevronUpIcon}
                aria-label={state.chatIsOpen ? 'Collapse' : 'Expand'}
                tooltipDirection={state.chatIsOpen ? 'sw' : 'w'}
                onClick={
                  state.chatIsOpen
                    ? () => manager.closeChat()
                    : () => manager.openChat(thread, state.currentView, 'header', state.chatVisibleSettingPath)
                }
                data-hotkey="Shift+Z"
                className={styles.actionButton}
              />
            )}
          </>
        )}
      </div>
      <StaffDialogs
        dialogType={props.showStaffDialog}
        staffDialogRef={props.staffDialogRef}
        onDismiss={() => props.setShowStaffDialog(DialogType.None)}
      />
      {showPersonalInstructionsDialog && (
        <PersonalInstructionsDialog
          returnFocusRef={menuAnchorRef}
          onDismiss={() => setShowPersonalInstructionsDialog(false)}
        />
      )}
    </Box>
  )
}

const ReturnToCurrentThreadButton = () => {
  const {mode} = useChatState()
  const manager = useChatManager()

  return (
    <Button
      leadingVisual={ArrowLeftIcon}
      variant="invisible"
      onClick={() => manager.viewCurrentThread()}
      sx={{
        marginLeft: mode === 'assistive' ? '-8px' : undefined,
      }}
      className={styles.actionButton}
    >
      Back
    </Button>
  )
}

interface ThreadOptionButtonProps {
  handleDelete: () => void
  thread?: CopilotChatThread | null
  setShowStaffDialog: (value: DialogType) => void
  setShowPersonalInstructionsDialog: (value: boolean) => void
  anchorRef: RefObject<HTMLButtonElement>
}

export const ThreadOptionButton = (props: ThreadOptionButtonProps) => {
  const [open, setOpen] = useState(false)
  const {chatVisibleSettingPath, mode, repoCustomInstructionsEnabled, currentReferences, optedInToUserFeedback} =
    useChatState()

  const manager = useChatManager()
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const noFloatingButton = isFeatureEnabled('copilot_no_floating_button')
  const showPromptDialog = copilotFeatureFlags.staffPromptDialog

  const customInstructions = getCustomInstructionsFromReferences(currentReferences)

  const handleClickViewAll = useCallback(() => {
    // We might not have tried to load any threads but the latest one yet.
    void manager.fetchThreads()

    manager.viewAllThreads()
    sendEvent('copilot.view-conversations-clicked')
  }, [manager])

  const handleSelectDelete = () => {
    props.handleDelete()
    setOpen(false)
  }

  const userIsStaff = useMemo(() => {
    return isStaff()
  }, [])

  const deleteDisabled = !props.thread

  return (
    <>
      <ActionMenu open={open} onOpenChange={() => setOpen(prev => !prev)} anchorRef={props.anchorRef}>
        <ActionMenu.Anchor>
          <IconButton
            icon={KebabHorizontalIcon}
            variant="invisible"
            aria-label="Conversation options"
            tooltipDirection="n"
            className={styles.actionButton}
          />
        </ActionMenu.Anchor>
        <ActionMenuOverlay>
          <ActionList>
            {customInstructions.length > 0 && (
              <ActionList.Item
                onSelect={() => {
                  manager.toggleRepoCustomInstructions(!repoCustomInstructionsEnabled)
                }}
              >
                <ActionList.LeadingVisual>
                  <NoteIcon />
                </ActionList.LeadingVisual>
                {repoCustomInstructionsEnabled ? 'Disable custom instructions' : 'Enable custom instructions'}
              </ActionList.Item>
            )}
            <ActionList.Item
              variant={deleteDisabled ? 'default' : 'danger'}
              onSelect={handleSelectDelete}
              disabled={deleteDisabled}
              aria-describedby="delete-conversation-description"
            >
              <ActionList.LeadingVisual>
                <TrashIcon />
              </ActionList.LeadingVisual>
              Delete conversation
              <p id="delete-conversation-description" className="sr-only">
                This is a destructive action that cannot be undone
              </p>
            </ActionList.Item>
            <ActionList.Divider />
            <ActionList.Item onSelect={() => props.setShowPersonalInstructionsDialog(true)}>
              <ActionList.LeadingVisual>
                <ToolsIcon />
              </ActionList.LeadingVisual>
              Personal instructions
            </ActionList.Item>
            {mode === 'assistive' && (
              <ActionList.Item onSelect={handleClickViewAll}>
                <ActionList.LeadingVisual>
                  <HistoryIcon />
                </ActionList.LeadingVisual>
                View all conversations
              </ActionList.Item>
            )}
            {optedInToUserFeedback && (
              <ActionList.Item
                onSelect={() => {
                  feedbackRef.current?.openDialog()
                  sendEvent('dotcom_chat.activate', {target: 'META_CONTEXT_MENU_GIVE_FEEDBACK', mode: 'assistive'})
                }}
              >
                <ActionList.LeadingVisual>
                  <CommentDiscussionIcon />
                </ActionList.LeadingVisual>
                Give feedback
              </ActionList.Item>
            )}
            {mode === 'assistive' && !noFloatingButton && (
              <ActionList.Item
                onSelect={() => {
                  manager.hideChat(chatVisibleSettingPath)
                }}
              >
                <ActionList.LeadingVisual>
                  <EyeClosedIcon />
                </ActionList.LeadingVisual>
                Hide Copilot chat
              </ActionList.Item>
            )}
            {userIsStaff && (
              <ActionList.Item onSelect={() => props.setShowStaffDialog(DialogType.Experiments)}>
                <ActionList.LeadingVisual>
                  <BeakerIcon />
                </ActionList.LeadingVisual>
                Configure experiments
                <ActionList.TrailingVisual>
                  <Label variant="attention">Staff</Label>
                </ActionList.TrailingVisual>
              </ActionList.Item>
            )}
            {userIsStaff && showPromptDialog && (
              <ActionList.Item onSelect={() => props.setShowStaffDialog(DialogType.Prompt)}>
                <ActionList.LeadingVisual>
                  <BeakerIcon />
                </ActionList.LeadingVisual>
                Adjust prompts
                <ActionList.TrailingVisual>
                  <Label variant="attention">Staff</Label>
                </ActionList.TrailingVisual>
              </ActionList.Item>
            )}
          </ActionList>
        </ActionMenuOverlay>
      </ActionMenu>
      <ConversationFeedbackDialog ref={feedbackRef} mode="assistive" />
    </>
  )
}

try{ Header.displayName ||= 'Header' } catch {}
try{ ReturnToCurrentThreadButton.displayName ||= 'ReturnToCurrentThreadButton' } catch {}
try{ ThreadOptionButton.displayName ||= 'ThreadOptionButton' } catch {}